import React, { useState, useEffect } from 'react';
import { Photo } from '../../../../types';
import { PhotoDisplayData } from '../../../../types/PhotoDisplayData';
import { validateMediaImageUrl } from '../../../../utils/imageLoader';
import { generateVideoThumbnail } from '../../../../utils/videoUtils';
import { useUIStore } from '../../../../stores/useUIStore';
import { useAuthStore } from '../../../../stores/useAuthStore';
import { PhotoTileFrame } from './PhotoTileFrame';
import { VideoPlayOverlay } from './VideoPlayOverlay';
import '../../../ui/LoadingSpinner/LoadingSpinner.css';

interface VideoPhotoItemProps {
    photo: Photo;
    photoData?: PhotoDisplayData;
    index: number;
    totalPhotos?: number;
}

const VideoPhotoItem: React.FC<VideoPhotoItemProps> = ({
    photo,
    photoData,
    index,
    totalPhotos,
}) => {
    const { openMediaModal } = useUIStore();
    const revalidateSessionAfterMediaError = useAuthStore((state) => state.revalidateSessionAfterMediaError);

    const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
    const [thumbnailLoading, setThumbnailLoading] = useState(false);
    const [thumbnailError, setThumbnailError] = useState(false);

    useEffect(() => {
        setThumbnailLoading(true);
        const videoUrl = validateMediaImageUrl(photo.url);

        generateVideoThumbnail(videoUrl, {
            width: 320,
            height: 320,
            timeOffset: 0.5
        })
        .then(thumbnail => {
            setVideoThumbnail(thumbnail);
            setThumbnailLoading(false);
        })
        .catch(error => {
            console.error('Failed to generate video thumbnail:', error);
            setThumbnailError(true);
            setThumbnailLoading(false);
            void revalidateSessionAfterMediaError();
        });
    }, [photo.url, revalidateSessionAfterMediaError]);

    const handleVideoClick = () => {
        const videoUrl = validateMediaImageUrl(photo.url);
        openMediaModal(videoUrl, 'video');
    };

    let thumbnailContent: React.ReactNode;
    if (thumbnailLoading) {
        thumbnailContent = (
            <div className="video-thumbnail-loading">
                <div className="loading-spinner"></div>
                <p>Generating thumbnail...</p>
            </div>
        );
    } else if (thumbnailError) {
        thumbnailContent = (
            <div className="video-thumbnail-error">
                <VideoPlayOverlay />
                <p>Video Preview</p>
            </div>
        );
    } else if (videoThumbnail) {
        thumbnailContent = (
            <>
                <img
                    src={videoThumbnail}
                    alt={photo.caption ? photo.caption : `Memory Video ${index + 1}`}
                    className="video-thumbnail-image"
                    draggable={false}
                />
                <VideoPlayOverlay />
            </>
        );
    } else {
        thumbnailContent = (
            <div className="video-thumbnail-fallback">
                <VideoPlayOverlay />
            </div>
        );
    }

    return (
        <PhotoTileFrame
            photo={photo}
            photoData={photoData}
            index={index}
            totalPhotos={totalPhotos}
            wrapperClassName="video-wrapper video-thumbnail-container"
            ariaLabel={photo.caption ? `Play video: ${photo.caption}` : `Play Memory Video ${index + 1}`}
            onActivate={handleVideoClick}
        >
            {thumbnailContent}
        </PhotoTileFrame>
    );
};

export default VideoPhotoItem;
